import React from "react";
import { Box } from "@mui/material";
import { DotLegalAlert, DotLegalDialog, DotLegalHeader, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import { useTranslation } from "@localization/useTranslation";
import { riskAreaScaleOptions, useRiskAreaDialogDataMapping } from "./RiskAreaDialog.hooks";
import { RiskAreaSaveModel } from "./RiskAreaDialog.types";
import { useRiskAreaDialogStyles } from "./RiskAreaDialog.styles";
import { RiskLevelLabelList } from "./riskLevels/RiskLevelLabelList";
import { RiskAreaMatrix } from "./riskAreaMatrix/RiskAreaMatrix";
import { FrameChangeConfirmationStep } from "./frameChange/FrameChangeConfirmationStep";
import DialogHeaderWithInformation from "@common/components/dialogHeaderWithInformation/DialogHeaderWithInformation";

export interface IRiskAreaDialog {
    onHideDialog: () => void;
    onSave: (response: string) => void;
    selectedRiskArea: RiskAreaSaveModel | undefined;
    disableTransition?: boolean;
}

function RiskAreaDialog(props: IRiskAreaDialog) {
    const { translateString } = useTranslation();
    const styles = useRiskAreaDialogStyles();
    const {
        inProgress,
        viewModel,
        errors,
        isEdit,
        setName,
        setResponsible,
        responsibleOptions,
        isLoadingResponsibles,
        setAssessmentLevel,
        assessmentLevelOptions,
        setScale,
        setAppetite,
        setConsequenceLevel,
        setProbabilityLevel,
        setRiskLevel,
        setRiskLevelIndicator,
        setMatrixCell,
        validateAndSave,
        canChangeFrame,
        cellBadges,
        overAppetite,
        step,
        backToConfigure,
        confirmFrameChange,
        changeReason,
        changeReasonEntered,
        reasonError,
        frameChangeImpact,
        isLoadingConfirmation,
        canConfirm,
        scaleMapping,
    } = useRiskAreaDialogDataMapping(props);

    const isConfirming = step === "confirm";

    const scaleOptions = riskAreaScaleOptions.map((scale) => ({ id: scale.toString(), name: `1-${scale}` }));

    const notResponsibleAlert = <DotLegalAlert severity="info" warningText={translateString("riskAreaFrameChangeNotResponsible")} marginBottom={2} />;

    const configureHeader = isEdit() ? translateString("configureRiskArea", { name: viewModel.name }) : translateString("createRiskArea");

    const confirmHeader = scaleMapping.isReBasing
        ? translateString("riskAreaReBaseHeader", { name: viewModel.name, from: scaleMapping.fromScale, to: scaleMapping.toScale })
        : translateString("riskAreaFrameChangeHeader", { name: viewModel.name });

    return (
        <DotLegalDialog
            inProgress={inProgress}
            size={isConfirming ? "lg" : "xxl"}
            isLoading={isConfirming && isLoadingConfirmation}
            okButtonDisabled={isConfirming && !canConfirm}
            buttonOkText={isConfirming ? translateString("riskAreaFrameChangeConfirm") : isEdit() ? translateString("save") : translateString("create")}
            secondaryButtonText={isConfirming ? translateString("previous") : undefined}
            onSecondaryButtonClick={isConfirming ? backToConfigure : undefined}
            header={
                isConfirming ? (
                    confirmHeader
                ) : (
                    <DialogHeaderWithInformation header={configureHeader} information={isEdit() ? undefined : translateString("riskAreaNameTooltip")} />
                )
            }
            onOkClick={isConfirming ? confirmFrameChange : validateAndSave}
            onDialogClose={props.onHideDialog}
            open
            disableTransition={props.disableTransition}
            disableBackdropOnClose={isConfirming}
            disableBackdropOnCloseIfValueChange={viewModel}
        >
            {isConfirming && (
                <Box sx={styles.confirmation}>
                    <FrameChangeConfirmationStep
                        impact={frameChangeImpact}
                        isReBasing={scaleMapping.isReBasing}
                        fromScale={scaleMapping.fromScale}
                        toScale={scaleMapping.toScale}
                        consequenceRows={scaleMapping.consequenceRows}
                        probabilityRows={scaleMapping.probabilityRows}
                        consequenceValid={scaleMapping.consequenceValid}
                        probabilityValid={scaleMapping.probabilityValid}
                        onConsequenceTargetChange={scaleMapping.setConsequenceTarget}
                        onProbabilityTargetChange={scaleMapping.setProbabilityTarget}
                        collapseWarnings={scaleMapping.collapseWarnings}
                    />

                    {/* One reason for the change, whichever kind it is: it justifies re-cutting the instrument,
                        not the particular mapping or matrix cell that did it. */}
                    <DotLegalTextField
                        debounce={false}
                        multiline
                        rows={3}
                        label={translateString("riskAreaFrameChangeReason")}
                        toolTipText={translateString("riskAreaFrameChangeReasonTooltip")}
                        value={changeReason}
                        onChange={changeReasonEntered}
                        errorText={reasonError}
                    />
                </Box>
            )}

            {!isConfirming && (
                <Box sx={styles.cards}>
                    <DotLegalSection>
                        <Box sx={styles.topRow}>
                            <Box sx={styles.topRowField}>
                                <DotLegalTextField
                                    debounce={false}
                                    size="small"
                                    label={translateString("riskAreaName")}
                                    toolTipText={translateString("riskAreaNameTooltip")}
                                    value={viewModel.name}
                                    onChange={setName}
                                    errorText={errors.nameError}
                                />
                            </Box>
                            <Box sx={styles.topRowField}>
                                <DotLegalSelect
                                    label={translateString("responsible")}
                                    options={responsibleOptions}
                                    selectedItem={viewModel.responsibleId}
                                    onChange={setResponsible}
                                    isLoading={isLoadingResponsibles}
                                    noOptionsLabel={translateString("noOptions")}
                                    errorText={errors.responsibleError}
                                />
                            </Box>
                            <Box sx={styles.topRowField}>
                                <DotLegalSelect
                                    label={translateString("riskAreaAssessmentLevel")}
                                    toolTipText={translateString("riskAreaAssessmentLevelTooltip")}
                                    options={assessmentLevelOptions}
                                    selectedItem={viewModel.assessmentLevel.toString()}
                                    onChange={(value) => value && setAssessmentLevel(Number(value))}
                                    disableClearable
                                    disableSearch
                                    noOptionsLabel={translateString("noOptions")}
                                />
                            </Box>
                            <Box sx={styles.topRowField}>
                                <DotLegalSelect
                                    label={translateString("riskAreaScale")}
                                    toolTipText={translateString("riskAreaScaleTooltip")}
                                    options={scaleOptions}
                                    selectedItem={viewModel.scale.toString()}
                                    disabled={!canChangeFrame}
                                    onChange={(value) => value && setScale(Number(value))}
                                    disableClearable
                                    disableSearch
                                    noOptionsLabel={translateString("noOptions")}
                                />
                            </Box>
                            <Box sx={styles.topRowField}>
                                <DotLegalTextField
                                    debounce={false}
                                    size="small"
                                    disabled={!canChangeFrame}
                                    label={`${translateString("riskAreaAppetite")} (1-${viewModel.scale})`}
                                    // A scale change clears the appetite, so the tooltip says why it has to be
                                    // chosen again rather than leaving an empty required field unexplained.
                                    toolTipText={translateString(scaleMapping.isReBasing ? "riskAreaReBaseAppetiteTooltip" : "riskAreaAppetiteTooltip")}
                                    onlyNumber="integer"
                                    minNumberValue={1}
                                    maxNumberValue={viewModel.scale}
                                    value={viewModel.appetite?.toString() ?? ""}
                                    onChange={(value) => setAppetite(value === "" ? null : Number(value))}
                                    errorText={errors.appetiteError}
                                />
                                {/* The question a risk owner actually has about an appetite is how many risks sit
                                    above it, so it is answered while the threshold is being chosen. */}
                                {overAppetite && overAppetite.total > 0 && (
                                    <Box sx={styles.appetiteReadout}>
                                        {translateString("riskAreaAppetiteOverCount", { count: overAppetite.count, total: overAppetite.total })}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </DotLegalSection>

                    <Box sx={styles.contentColumns}>
                        <DotLegalSection>
                            <DotLegalHeader headerStyle="small" color="primary" marginBottom={0.5}>
                                {translateString("riskAreaZoneLabels")}
                            </DotLegalHeader>
                            <Box sx={styles.zoneSubtitle}>{translateString("riskAreaZoneLabelsSubtitle")}</Box>
                            <Box sx={styles.levelsCard}>
                                <Box sx={styles.consequenceProbabilityRow}>
                                    <RiskLevelLabelList
                                        title={translateString("riskAreaConsequenceLevels")}
                                        values={viewModel.consequenceLevels}
                                        onChange={setConsequenceLevel}
                                    />
                                    <RiskLevelLabelList
                                        title={translateString("riskAreaProbabilityLevels")}
                                        values={viewModel.probabilityLevels}
                                        onChange={setProbabilityLevel}
                                    />
                                </Box>
                                <Box sx={styles.consequenceProbabilityRow}>
                                    <RiskLevelLabelList
                                        title={translateString("riskAreaRiskLevels")}
                                        values={viewModel.riskLevels}
                                        onChange={setRiskLevel}
                                        indicators={viewModel.riskLevelIndicators}
                                        onIndicatorChange={setRiskLevelIndicator}
                                    />
                                </Box>
                            </Box>
                        </DotLegalSection>
                        <DotLegalSection>
                            <DotLegalHeader toolTip={translateString("riskMatrixInfo")} headerStyle="small" color="primary" marginBottom={0.5}>
                                {translateString("riskAreaZoneCriteria")}
                            </DotLegalHeader>
                            <Box sx={styles.zoneSubtitle}>{`${translateString("riskAreaMatrixSubtitle")} (1-${viewModel.scale})`}</Box>
                            {!canChangeFrame && notResponsibleAlert}
                            <RiskAreaMatrix
                                scale={viewModel.scale}
                                consequenceLevels={viewModel.consequenceLevels}
                                probabilityLevels={viewModel.probabilityLevels}
                                riskLevelIndicators={viewModel.riskLevelIndicators}
                                riskLevels={viewModel.riskLevels}
                                matrix={viewModel.matrix}
                                onCellChange={setMatrixCell}
                                highlightEmptyCells={!!errors.matrixError}
                                disabled={!canChangeFrame}
                                cellBadges={cellBadges}
                            />
                            {errors.matrixError && <Box sx={styles.matrixErrorText}>{errors.matrixError}</Box>}
                        </DotLegalSection>
                    </Box>
                </Box>
            )}
        </DotLegalDialog>
    );
}

export default RiskAreaDialog;
